import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import ScheduleEditor from "@/components/ScheduleEditor";
import {
  Users, Clock, Pencil, WarningCircle, Pulse, Phone, Target,
  Lightning, TrendUp, ChartBar, CaretRight, CaretDown, Gear,
  UserCircle, CheckCircle, XCircle
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  active: { color: "#16A34A", bg: "bg-green-50", border: "border-green-200", label: "Active", dot: "bg-green-500" },
  idle: { color: "#F59E0B", bg: "bg-amber-50", border: "border-amber-200", label: "Idle", dot: "bg-amber-400" },
  absent: { color: "#EF4444", bg: "bg-red-50", border: "border-red-200", label: "Absent", dot: "bg-red-500" },
  offline: { color: "#9CA3AF", bg: "bg-gray-50", border: "border-gray-200", label: "Offline", dot: "bg-gray-400" },
  not_configured: { color: "#D97706", bg: "bg-amber-50", border: "border-amber-200", label: "Not Set", dot: "bg-amber-300" },
};

const KPI_STATE = {
  green: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", dot: "bg-emerald-500", label: "GREEN" },
  amber: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", dot: "bg-amber-500", label: "AMBER" },
  red: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", dot: "bg-red-500", label: "RED" },
};

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

const ROLE_LABELS = {
  opportunity_finder: "Opportunity Finder",
  candidate_finder: "Candidate Finder",
  team_leader: "Team Leader",
  admin: "Administrator",
  senior_recruitment_partner: "Senior Recruitment Partner",
};

// Which KPI columns to show per role
const OPP_COLS = ["calls_attempted", "calls_connected", "talk_minutes", "leads_created", "qualified_leads", "emails_sent"];
const CAND_COLS = ["candidate_searches", "candidates_added", "candidates_contacted", "candidates_submitted", "vacancy_matches", "emails_sent"];

const COL_LABELS = {
  calls_attempted: "Calls", calls_connected: "Connected", talk_minutes: "Talk",
  leads_created: "Opps", qualified_leads: "Qualified", emails_sent: "Emails",
  candidate_searches: "Searches", candidates_added: "Sourced", candidates_contacted: "Contacted",
  candidates_submitted: "Submitted", vacancy_matches: "Matches",
};

function KpiBar({ score, state }) {
  const cfg = KPI_STATE[state] || KPI_STATE.amber;
  return (
    <div className="flex items-center gap-1.5 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${cfg.dot}`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
      <span className={`text-[10px] font-bold ${cfg.text} min-w-[30px] text-right`}>{score}%</span>
    </div>
  );
}

function DrillDownModal({ userId, date, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    const params = date ? `?date=${date}` : "";
    api.get(`/kpi/individual/${userId}${params}`).then(({ data: d }) => setData(d)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [open, userId, date]);

  if (!open) return null;
  const kpi = data?.kpi || {};
  const profile = data?.profile || {};
  const actuals = data?.actuals || {};
  const isOpp = data?.user?.role === "opportunity_finder";
  const isCand = data?.user?.role === "candidate_finder";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="kpi-drilldown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCircle size={22} className="text-[#C5A059]" />
            {data?.user?.name || "Loading..."} — Performance Detail
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
        ) : data ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className={`text-3xl font-black ${KPI_STATE[kpi.state]?.text || "text-[#0F172A]"}`}>{kpi.score}%</div>
              <div>
                <Badge className={`${KPI_STATE[kpi.state]?.bg} ${KPI_STATE[kpi.state]?.border} ${KPI_STATE[kpi.state]?.text} text-xs`}>{KPI_STATE[kpi.state]?.label}</Badge>
                <div className="text-xs text-[#6B7280] mt-1">{ROLE_LABELS[data.user?.role] || data.user?.role} | {Math.round(data.login_mins || 0)}m logged of {data.scheduled_mins || 0}m scheduled</div>
              </div>
            </div>
            <Separator />
            {/* Component breakdown with target vs actual */}
            <div>
              <h4 className="text-sm font-semibold text-[#0F172A] mb-2">KPI Components — Why this score</h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(kpi.components || {}).map(([key, val]) => {
                  const weight = profile.weights?.[key] || 0;
                  if (!weight) return null;
                  return (
                    <div key={key} className="flex items-center gap-2 bg-[#F9FAFB] rounded-md px-3 py-2">
                      <div className="flex-1">
                        <div className="text-xs font-medium text-[#374151] capitalize">{key.replace(/_/g, " ")}</div>
                        <div className="text-[10px] text-[#9CA3AF]">Weight: {weight}%</div>
                      </div>
                      <div className={`text-sm font-bold ${val >= 80 ? "text-emerald-600" : val >= 50 ? "text-amber-600" : "text-red-600"}`}>{val}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <Separator />
            {/* Target vs Actual */}
            <div>
              <h4 className="text-sm font-semibold text-[#0F172A] mb-2">Targets vs Actuals</h4>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(profile.targets || {}).map(([key, target]) => {
                  const actual = actuals[key] || 0;
                  const pct = target > 0 ? Math.round((actual / target) * 100) : 100;
                  const variance = actual - target;
                  return (
                    <div key={key} className="bg-[#F9FAFB] rounded-md px-3 py-2">
                      <div className="text-[10px] text-[#6B7280] capitalize">{key.replace(/_/g, " ")}</div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-sm font-bold text-[#0F172A]">{typeof actual === "number" && actual % 1 !== 0 ? actual.toFixed(1) : actual}</span>
                        <span className="text-[10px] text-[#9CA3AF]">/ {target}</span>
                      </div>
                      <div className={`text-[10px] font-medium ${variance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {variance >= 0 ? "+" : ""}{typeof variance === "number" && variance % 1 !== 0 ? variance.toFixed(1) : variance} ({pct}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Call timeline for opp finders */}
            {data.calls?.length > 0 && (
              <>
                <Separator />
                <div>
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-2">Call Log</h4>
                  <div className="max-h-[180px] overflow-y-auto space-y-1">
                    {data.calls.slice(0, 30).map((call, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-[#F3F4F6]">
                        <span className="text-[#9CA3AF] min-w-[50px]">{call.created_at?.split("T")[1]?.slice(0, 5) || ""}</span>
                        <Phone size={11} className="text-[#6B7280]" />
                        <span className="text-[#0F172A] font-medium truncate">{call.contact_name || call.phone_number || "Unknown"}</span>
                        <Badge variant="outline" className={`text-[9px] ${call.call_status === "answered" || call.call_status === "connected" ? "border-emerald-300 text-emerald-700" : ""}`}>
                          {call.call_status || "dialled"}
                        </Badge>
                        <span className="text-[#9CA3AF]">{call.duration ? `${call.duration}s` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {/* Activity timeline */}
            {data.timeline?.length > 0 && (
              <>
                <Separator />
                <div>
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-2">Activity Timeline</h4>
                  <div className="max-h-[160px] overflow-y-auto space-y-1">
                    {data.timeline.slice(0, 40).map((act, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-[#F3F4F6]">
                        <span className="text-[#9CA3AF] min-w-[50px]">{act.created_at?.split("T")[1]?.slice(0, 5) || ""}</span>
                        <Badge variant="outline" className="text-[9px]">{act.action}</Badge>
                        <span className="text-[#6B7280] truncate">{act.resource_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ProfileConfigModal({ open, onClose }) {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [editRole, setEditRole] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/kpi/profiles").then(({ data }) => setProfiles(data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [open]);

  const save = async (role) => {
    setSaving(role);
    try {
      await api.put(`/kpi/profiles/${role}`, profiles[role]);
      toast.success(`${role} profile updated`);
    } catch { toast.error("Save failed"); }
    setSaving("");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="kpi-config-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Gear size={20} className="text-[#C5A059]" /> KPI Targets Configuration</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {Object.entries(profiles).map(([role, profile]) => (
              <div key={role} className="border border-[#E5E7EB] rounded-lg p-4">
                <div className="flex items-center justify-between mb-1 cursor-pointer" onClick={() => setEditRole(editRole === role ? "" : role)}>
                  <div>
                    <div className="text-sm font-semibold text-[#0F172A]">{ROLE_LABELS[role] || role}</div>
                    <div className="text-xs text-[#6B7280]">{profile.label}</div>
                  </div>
                  <CaretRight size={16} className={`text-[#9CA3AF] transition-transform ${editRole === role ? "rotate-90" : ""}`} />
                </div>
                {editRole === role && (
                  <div className="space-y-3 mt-3 pt-3 border-t border-[#F3F4F6]">
                    <div>
                      <div className="text-xs font-medium text-[#374151] mb-2">Daily Targets</div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(profile.targets || {}).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-2">
                            <label className="text-[10px] text-[#6B7280] flex-1 capitalize">{key.replace(/_/g, " ")}</label>
                            <Input type="number" className="h-7 w-16 text-xs" value={val} onChange={e => setProfiles(p => ({
                              ...p, [role]: { ...p[role], targets: { ...p[role].targets, [key]: parseInt(e.target.value) || 0 } }
                            }))} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-[#374151] mb-2">Weights (%)</div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(profile.weights || {}).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-2">
                            <label className="text-[10px] text-[#6B7280] flex-1 capitalize">{key.replace(/_/g, " ")}</label>
                            <Input type="number" className="h-7 w-16 text-xs" value={val} onChange={e => setProfiles(p => ({
                              ...p, [role]: { ...p[role], weights: { ...p[role].weights, [key]: parseInt(e.target.value) || 0 } }
                            }))} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => save(role)} disabled={saving === role} data-testid={`save-profile-${role}`}>
                      {saving === role ? "Saving..." : "Save Profile"}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TeamStatusPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "team_leader";
  const isAdmin = user?.role === "admin";
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [drillUserId, setDrillUserId] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserName, setEditUserName] = useState("");

  const load = useCallback(async () => {
    try {
      const params = date ? `?date=${date}` : "";
      const { data } = await api.get(`/kpi/live-team${params}`);
      setKpiData(data);
    } catch (err) { console.warn("Load error:", err?.message); }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  const members = kpiData?.members || [];
  const totals = kpiData?.totals || {};
  const now = new Date();
  const currentHour = now.getHours();

  // Group members by role type for display
  const oppFinders = members.filter(m => m.role === "opportunity_finder");
  const candFinders = members.filter(m => m.role === "candidate_finder");
  const leaders = members.filter(m => m.role === "team_leader" || m.role === "admin");

  const counts = {
    active: members.filter(m => m.status === "active").length,
    idle: members.filter(m => m.status === "idle").length,
    absent: members.filter(m => m.status === "absent").length,
    offline: members.filter(m => m.status === "offline").length,
    not_configured: members.filter(m => m.status === "not_configured").length,
  };

  const renderTimeline = (m) => {
    const dayName = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const daySchedule = m.schedule?.days?.[dayName];
    const isEnabled = daySchedule?.enabled;
    const startHour = isEnabled && daySchedule.start ? parseInt(daySchedule.start.split(":")[0]) : null;
    const endHour = isEnabled && daySchedule.end ? parseInt(daySchedule.end.split(":")[0]) : null;
    const lunchStartH = daySchedule?.lunch_start ? parseInt(daySchedule.lunch_start.split(":")[0]) : null;
    const lunchEndH = daySchedule?.lunch_end ? parseInt(daySchedule.lunch_end.split(":")[0]) : null;
    const breakHours = new Set();
    (daySchedule?.breaks || []).forEach(b => { if (b.time) breakHours.add(parseInt(b.time.split(":")[0])); });

    return HOURS.map(h => {
      let cls = "bg-gray-50 border-gray-100";
      if (!m.configured) { cls = "bg-gray-50 border-dashed border-gray-200"; }
      else if (startHour !== null && h >= startHour && h < endHour) {
        if (lunchStartH !== null && h >= lunchStartH && h < lunchEndH) cls = "bg-amber-100 border-amber-200";
        else if (breakHours.has(h)) cls = "bg-orange-100 border-orange-200";
        else if (m.status === "active" && h === currentHour) cls = "bg-green-400 border-green-500";
        else if (m.status === "absent" && h <= currentHour) cls = "bg-red-200 border-red-300";
        else cls = "bg-blue-200 border-blue-300";
      }
      return (
        <td key={h} className="py-1 px-0.5">
          <div className={`h-5 rounded-sm border ${cls} ${h === currentHour ? "ring-1 ring-[#1E3A8A]" : ""}`} />
        </td>
      );
    });
  };

  const renderMemberRow = (m, cols) => {
    const scfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.offline;
    const kcfg = KPI_STATE[m.kpi?.state] || KPI_STATE.amber;
    const isExpanded = expandedId === m.id;
    const a = m.actuals || {};

    return (
      <tr key={m.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFBFC] transition-colors" data-testid={`team-member-${m.id}`}>
        {/* Name + status */}
        <td className="py-2 px-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-shrink-0">
              <div className="w-7 h-7 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-[10px] font-bold">
                {m.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${scfg.dot}`} />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-[#0F172A] truncate">{m.name}</div>
              <div className="text-[9px] text-[#9CA3AF]">{m.teams?.[0] || ""}</div>
            </div>
          </div>
        </td>
        {/* Online status */}
        <td className="py-2 px-1 text-center">
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${scfg.bg} ${scfg.border} border`} style={{ color: scfg.color }}>
            {scfg.label}
          </span>
        </td>
        {/* Schedule & time */}
        <td className="py-2 px-1 text-center text-[10px] text-[#6B7280]">{m.scheduled_mins ? `${m.scheduled_mins}m` : "—"}</td>
        <td className="py-2 px-1 text-center text-[10px] text-[#6B7280]">{m.login_mins ? `${Math.round(m.login_mins)}m` : "—"}</td>
        {/* KPI */}
        <td className="py-2 px-1">
          <div className="flex items-center gap-1">
            <KpiBar score={m.kpi?.score || 0} state={m.kpi?.state || "red"} />
            <Badge variant="outline" className={`text-[8px] px-1 py-0 ${kcfg.bg} ${kcfg.border} ${kcfg.text}`}>{kcfg.label}</Badge>
          </div>
        </td>
        {/* Role-specific activity columns */}
        {cols.map(col => (
          <td key={col} className="py-2 px-1 text-center text-[10px] text-[#0F172A]">
            {col === "talk_minutes" ? `${a[col] || 0}m` : (a[col] || 0)}
          </td>
        ))}
        {/* Actions */}
        <td className="py-2 px-1 text-center">
          <div className="flex items-center gap-1 justify-center">
            <button onClick={() => setDrillUserId(m.id)} className="text-[#9CA3AF] hover:text-[#0F172A] p-0.5" title="View detail" data-testid={`drill-down-${m.id}`}>
              <ChartBar size={14} />
            </button>
            {canEdit && (
              <button onClick={() => { setEditUserId(m.id); setEditUserName(m.name); }} className="text-[#9CA3AF] hover:text-[#1E3A8A] p-0.5" title="Edit schedule" data-testid={`edit-schedule-${m.id}`}>
                <Pencil size={13} />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderRoleSection = (title, roleMembers, cols) => {
    if (roleMembers.length === 0) return null;
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="px-4 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <h3 className="text-xs font-semibold text-[#374151] uppercase tracking-wide">{title} ({roleMembers.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid={`kpi-table-${title.toLowerCase().replace(/\s/g, "-")}`}>
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="py-2 px-2 text-[9px] font-semibold text-[#6B7280] uppercase w-[160px]">Member</th>
                <th className="py-2 px-1 text-[9px] font-semibold text-[#6B7280] uppercase text-center">Status</th>
                <th className="py-2 px-1 text-[9px] font-semibold text-[#6B7280] uppercase text-center">Sched</th>
                <th className="py-2 px-1 text-[9px] font-semibold text-[#6B7280] uppercase text-center">Logged</th>
                <th className="py-2 px-1 text-[9px] font-semibold text-[#6B7280] uppercase min-w-[130px]">KPI</th>
                {cols.map(col => (
                  <th key={col} className="py-2 px-1 text-[9px] font-semibold text-[#6B7280] uppercase text-center">{COL_LABELS[col] || col}</th>
                ))}
                <th className="py-2 px-1 w-[60px]" />
              </tr>
            </thead>
            <tbody>
              {roleMembers.map(m => renderMemberRow(m, cols))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="team-status-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <Pulse size={24} weight="duotone" className="text-[#C5A059]" />
            Team Status & Performance
          </h1>
          <p className="text-sm text-[#6B7280]">
            Live monitoring, KPIs, and working hours{kpiData?.date ? ` — ${kpiData.date}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9 w-[160px] text-sm" data-testid="status-date-picker" />
          {date && <Button variant="ghost" size="sm" onClick={() => setDate("")} className="text-xs">Today</Button>}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} className="gap-1" data-testid="kpi-targets-btn">
              <Gear size={14} /> Targets
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} className="gap-1" data-testid="refresh-btn">
            <Lightning size={14} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Status summary cards */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <div key={key} className={`${cfg.bg} ${cfg.border} border rounded-lg p-2.5 text-center`} data-testid={`status-count-${key}`}>
                <p className="text-xl font-bold" style={{ color: cfg.color }}>{counts[key] || 0}</p>
                <p className="text-[9px] text-[#6B7280] uppercase tracking-wide font-medium">{cfg.label}</p>
              </div>
            ))}
            <div className="bg-[#F0FDF4] border border-emerald-200 rounded-lg p-2.5 text-center">
              <p className="text-xl font-bold text-emerald-700">{totals.avg_kpi || 0}%</p>
              <p className="text-[9px] text-[#6B7280] uppercase tracking-wide font-medium">Avg KPI</p>
            </div>
          </div>

          {/* Working Hours Timeline */}
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
              <Clock size={16} weight="duotone" className="text-[#1E3A8A]" />
              Working Day Timeline — {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left py-1 px-2 text-[9px] font-semibold text-[#6B7280] uppercase w-[140px]">Worker</th>
                    {HOURS.map(h => (
                      <th key={h} className={`text-center py-1 px-0.5 text-[9px] font-medium min-w-[38px] ${h === currentHour ? "bg-blue-50 text-[#1E3A8A] font-bold" : "text-[#9CA3AF]"}`}>
                        {String(h).padStart(2, "0")}:00
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => {
                    const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.offline;
                    return (
                      <tr key={m.id} className="border-t border-[#F3F4F6]" data-testid={`timeline-row-${m.id}`}>
                        <td className="py-1 px-2">
                          <div className="flex items-center gap-1">
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                            <span className="font-medium text-[#0F172A] truncate text-[10px]">{m.name}</span>
                            {!m.configured && <WarningCircle size={10} className="text-amber-400 flex-shrink-0" />}
                          </div>
                        </td>
                        {renderTimeline(m)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-[#F3F4F6]">
              <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-blue-200 border border-blue-300" /><span className="text-[9px] text-[#6B7280]">Scheduled</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-green-400 border border-green-500" /><span className="text-[9px] text-[#6B7280]">Active</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-red-200 border border-red-300" /><span className="text-[9px] text-[#6B7280]">Absent</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-amber-100 border border-amber-200" /><span className="text-[9px] text-[#6B7280]">Lunch</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-orange-100 border border-orange-200" /><span className="text-[9px] text-[#6B7280]">Break</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-gray-50 border border-gray-100" /><span className="text-[9px] text-[#6B7280]">Off</span></div>
            </div>
          </div>

          {/* Role-specific KPI tables */}
          {renderRoleSection("Opportunity Finders", oppFinders, OPP_COLS)}
          {renderRoleSection("Candidate Finders", candFinders, CAND_COLS)}
          {renderRoleSection("Team Leaders & Admin", leaders, ["calls_attempted", "leads_created", "emails_sent"])}
        </>
      )}

      <DrillDownModal userId={drillUserId} date={date} open={!!drillUserId} onClose={() => setDrillUserId(null)} />
      <ProfileConfigModal open={showConfig} onClose={() => setShowConfig(false)} />
      {editUserId && (
        <ScheduleEditor userId={editUserId} userName={editUserName} open={!!editUserId} onClose={() => setEditUserId(null)} onSaved={load} />
      )}
    </div>
  );
}
